import React from 'react';
import {Breadcrumb} from '~/components/Breadcrumps';
import {Button} from '~/components/Button';
import {TagsList} from '~/components/Controls/Tags';
import {useConfirm} from '~/components/Dialogs';
import {useModals} from '~/components/Modal';
import {Progress} from '~/components/Progress';
import {BreadcrumbsResourcePage} from '~/components/ResourcePage';
import {TableRowActions, TableSort, TableSortDirection} from '~/components/Table';
import {useAbility} from '~/lib/ability';
import {getApiErrorMessage} from '~/lib/errors/apiError';
import {renderBoolean, renderToggle} from '~/lib/helpers';
import {Intent} from '~/lib/intent';
import {notify} from '~/lib/notify';
import {sortByString} from '~/lib/sort';
import {FeaturesAbility} from '~/modules/Features/ability';
import {FeatureForm, IFeatureFormActions, IFeatureFormProps, IFeatureFormValues} from '~/modules/Features/forms/FeatureForm';
import {featureValueAsStringList, FeatureType, IFeature} from '~/modules/Features/models/Feature';
import {useFeaturesQuery, useUpdateFeatureMutation} from '~/modules/Features/queries';

export const FeaturesView = () => {
    const ability = useAbility(FeaturesAbility);
    const modals = useModals();
    const confirm = useConfirm();
    const featuresQuery = useFeaturesQuery();
    const updateMutation = useUpdateFeatureMutation();
    const features = featuresQuery.data ?? [];

    const handleValueSubmit = (values: IFeatureFormValues, formActions: IFeatureFormActions, close: () => void) => {
        updateMutation.mutate({name: values.name, update: {value: values.value}}, {
            onSuccess: () => {
                notify.success(`Feature "${values.name}" updated.`);
                close();
            },
            onError: error => {
                const message = getApiErrorMessage(error, 'Failed to update feature');
                notify.error(message);
                formActions.setStatus(message);
            }
        });
    };

    const handleToggleEnabled = (feature: IFeature, enabled: boolean) => {
        confirm(`${enabled ? 'Enable' : 'Disable'} feature "${feature.name}"?`, {
            intent: enabled ? Intent.PRIMARY : Intent.WARNING,
            icon: <i className='fa fa-question'/>,
            onOk: () => updateMutation.mutate({name: feature.name, update: {enabled}}, {
                onSuccess: () => notify.success(`Feature "${feature.name}" ${enabled ? 'enabled' : 'disabled'}.`),
                onError: error => notify.error(getApiErrorMessage(error, 'Failed to update feature'))
            })
        });
    };

    const editModal = modals.create<IFeatureFormProps>(FeatureForm);

    return (
        <Breadcrumb breadcrumb={{title: 'Features'}}>
            <BreadcrumbsResourcePage title='Features'>
                <div className='layout-content-box'>
                    <TableSort<IFeature>
                        records={features}
                        header
                        defaultSort={{
                            sort: sortByString,
                            field: 'name',
                            direction: TableSortDirection.ASC
                        }}
                        columns={[
                            {
                                header: 'Name',
                                field: 'name',
                                sort: sortByString
                            },
                            {
                                header: 'Value',
                                cell: item => item.type === FeatureType.STRING_LIST
                                    ? <TagsList values={featureValueAsStringList(item)} maxItems={10}/>
                                    : String(item.value)
                            },
                            {
                                header: 'Enabled',
                                field: 'enabled',
                                small: true,
                                cell: item => ability.canEdit()
                                    ? renderToggle(item.enabled, next => handleToggleEnabled(item, next))
                                    : renderBoolean(item.enabled)
                            },
                            {
                                short: true,
                                cell: item => (
                                    <TableRowActions>
                                        {ability.canEdit() && (
                                            <Button
                                                small
                                                variant='menu'
                                                onClick={() => editModal.show({feature: item, onSubmit: handleValueSubmit})}
                                                leftIcon={<i className='fa fa-edit'/>}
                                            >
                                                Edit
                                            </Button>
                                        )}
                                    </TableRowActions>
                                )
                            }
                        ]}
                        placeholder={featuresQuery.isPending && <Progress/>}
                    />
                </div>
            </BreadcrumbsResourcePage>
        </Breadcrumb>
    );
};
